import { FC, useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useFormik } from "formik";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { toast } from "react-toastify";
import { useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";
import { makeSxStyles, useGetParamOrRedirect } from "ui";
import {
  adminRegisterInviteReqDtoSchema,
  applyAuthResponse,
  fetchAdminOrganizationContextControllerGetCurrent,
  useAdminAuthControllerRegisterInvite,
} from "service";
import { AdminRegisterInviteReqDto } from "service/api/estabrookSchemas";
import { AdRoutes } from "../../constants/ad-routes";
import { setPendingPostAuthRedirect } from "../../features/settings/utils/pending-post-auth-redirect";

const validationSchema = adminRegisterInviteReqDtoSchema
  .extend({
    password: z.string().min(8, "Password must be at least 8 characters"),
    confirmPassword: z
      .string()
      .min(8, "Password must be at least 8 characters"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords must match",
    path: ["confirmPassword"],
  });

const DEFAULT_HEADING = "Welcome to SnowSync Admin Panel";

const AcceptInvitePage: FC = () => {
  const navigate = useNavigate();
  const styles = useStyles();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();

  const params = useGetParamOrRedirect<{ email: string; token: string }>(
    ["email", "token"],
    AdRoutes.login,
  );

  const organizationName = searchParams.get("organizationName");

  useEffect(() => {
    if (params && !z.string().uuid().safeParse(params.token).success) {
      setLinkError(
        "This invitation link is invalid or has been corrupted. Please ask your admin to resend it.",
      );
    }
  }, [params]);

  const { mutate, isLoading } = useAdminAuthControllerRegisterInvite({
    onSuccess: async (data) => {
      if (!data?.data?.accessToken) return;

      // This page is only ever reached via an org-admin invite link, so
      // everyone landing here is an org admin - the only thing left to
      // decide is whether their organization already has a reply email.
      // The register-invite response doesn't reliably embed the
      // multi-tenancy `organization` field (that's populated for the full
      // login/session responses, not necessarily this one), so check the
      // authoritative "current org" endpoint directly with the token we
      // just received, rather than trusting data.data.organization - see
      // ESR-1614.
      //
      // This check MUST run before applyAuthResponse/navigate below, with
      // nothing async in between them: app.tsx force-logs-out any session
      // where isLoggedIn is true while the URL still looks like an invite
      // link (isInviteLink()/forceLogoutForInvite - a guard against reusing
      // a stale invite link while already logged in). If we awaited here
      // *after* applyAuthResponse, isLoggedIn can flip to true while the URL
      // is still /accept-invite, tripping that guard and kicking the user
      // right back to this same "set password" screen.
      let needsReplyEmail = false;
      try {
        const current = await fetchAdminOrganizationContextControllerGetCurrent(
          { headers: { authorization: `Bearer ${data.data.accessToken}` } },
        );
        const replyToEmail = current.data?.replyToEmail as
          | string
          | null
          | undefined;
        needsReplyEmail = !replyToEmail;
      } catch (error) {
        // Don't block the just-created account on this check failing -
        // worst case they set the reply email later in Settings.
        console.error("Couldn't check the organization's reply email:", error);
      }

      applyAuthResponse(data.data);
      toast.success("Account created successfully");
      if (needsReplyEmail) {
        // applyAuthResponse just flipped isLoggedIn, which swaps the whole
        // app from AuthRouter (this page's router, a standalone
        // <BrowserRouter> mounted only while logged out - see app.tsx) to
        // DashboardRouter - a completely separate router instance. A
        // navigate() call here can't reach it, so stash the destination and
        // let DashboardRouter's own Layout pick it up once it mounts.
        setPendingPostAuthRedirect(AdRoutes.setupReplyEmail);
      }
      navigate(AdRoutes.customer);
    },
    onError: (error) => {
      setLinkError(
        error?.errors?.[0]?.detail ??
          "This invitation has expired or is no longer valid.",
      );
    },
  });

  const {
    values,
    handleChange,
    handleBlur,
    errors,
    touched,
    isValid,
    submitForm,
  } = useFormik<AdminRegisterInviteReqDto>({
    initialValues: {
      email: params?.email ?? "",
      token: params?.token ?? "",
      password: "",
      confirmPassword: "",
    },
    enableReinitialize: true,
    validateOnMount: true,
    validationSchema: toFormikValidationSchema(validationSchema),
    onSubmit: (formValues) => {
      setLinkError(null);
      mutate({ body: formValues });
    },
  });

  return (
    <Container maxWidth="xs">
      <Box marginBottom="40px" display="flex" justifyContent="center">
        <img src="/snowsync-logo.png" alt="logo" style={{ width: "150px" }} />
      </Box>
      <Typography sx={styles.heading}>
        {organizationName ? `Welcome to ${organizationName}` : DEFAULT_HEADING}
      </Typography>
      <Box sx={styles.subheading}>
        <Typography sx={styles.subheadingText}>
          You’ve been invited to create an admin account.
        </Typography>
        <Typography sx={styles.subheadingText}>
          Email:{" "}
          <Box component="span" sx={styles.emailValue}>
            {params?.email}
          </Box>
        </Typography>
      </Box>
      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          submitForm();
        }}
      >
        <Grid container rowSpacing={3} direction="column">
          <Grid item>
            <Typography sx={styles.fieldLabel}>Set Your Password</Typography>
            <TextField
              id="password"
              value={values.password}
              onChange={handleChange}
              onBlur={handleBlur}
              type={showPassword ? "text" : "password"}
              error={touched.password && !!errors.password}
              helperText={touched.password && errors.password}
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => setShowPassword((prev) => !prev)}
                    edge="end"
                  >
                    {showPassword ? (
                      <Visibility sx={styles.visibilityIcon} />
                    ) : (
                      <VisibilityOff sx={styles.visibilityIcon} />
                    )}
                  </IconButton>
                ),
              }}
              fullWidth
            />
          </Grid>
          <Grid item>
            <Typography sx={styles.fieldLabel}>Confirm password</Typography>
            <TextField
              id="confirmPassword"
              value={values.confirmPassword}
              onChange={handleChange}
              onBlur={handleBlur}
              type={showConfirmPassword ? "text" : "password"}
              error={touched.confirmPassword && !!errors.confirmPassword}
              helperText={touched.confirmPassword && errors.confirmPassword}
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    edge="end"
                  >
                    {showConfirmPassword ? (
                      <Visibility sx={styles.visibilityIcon} />
                    ) : (
                      <VisibilityOff sx={styles.visibilityIcon} />
                    )}
                  </IconButton>
                ),
              }}
              fullWidth
            />
          </Grid>
          {linkError && (
            <Grid item>
              <Box sx={styles.errorBanner}>
                <Typography sx={styles.errorText}>{linkError}</Typography>
              </Box>
            </Grid>
          )}
          <Grid item>
            <Button
              type="submit"
              variant="contained"
              disabled={!params || !isValid || isLoading}
              fullWidth
              data-testid="create-account-button"
            >
              Create account
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

const useStyles = makeSxStyles(({ palette: { grey } }) => ({
  heading: {
    fontFamily: "Inter",
    fontWeight: 700,
    fontSize: "32px",
    lineHeight: "40px",
    color: grey[100],
    textAlign: "center",
    mb: "40px",
  },
  subheading: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    mb: "40px",
  },
  subheadingText: {
    fontFamily: "Inter",
    fontSize: "14px",
    lineHeight: "20px",
    letterSpacing: "0.1px",
    color: grey[100],
    textAlign: "center",
  },
  emailValue: {
    color: "#fff",
  },
  fieldLabel: {
    fontFamily: "Inter",
    fontSize: "14px",
    lineHeight: "20px",
    letterSpacing: "0.1px",
    color: grey[100],
    mb: "12px",
  },
  visibilityIcon: {
    color: "#C1C7CE",
  },
  errorBanner: {
    background: "#FFB4AB",
    borderRadius: "8px",
    padding: "14px 16px",
    boxShadow:
      "0px 1px 3px 0px rgba(0,0,0,0.30), 0px 4px 8px 3px rgba(0,0,0,0.15)",
  },
  errorText: {
    fontFamily: "Inter",
    fontSize: "14px",
    lineHeight: "20px",
    letterSpacing: "0.1px",
    color: "#690005",
  },
}));

export default AcceptInvitePage;
